import { useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from 'redux';
import { actionCreators } from "../../redux/index";
import { useNavigate } from 'react-router-dom';
import PageContext from './PageContext';
import AuditTrailIcon from '../../svgs/audit-trail-icon.svg';
import ChatIcon from '../../svgs/chat-icon.svg';
import moneyFormat from '../functions/moneyFormat';
import PageTooltip from './PageTooltip'; 
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { ReactComponent as SelectedIcon } from '../../svgs/selected.svg';
import { ReactComponent as UnselectedIcon } from '../../svgs/unselected.svg';
import PencilIcon from '../../svgs/pencil-icon.svg';
import PencilDisabledIcon from '../../svgs/pencil-icon-disabled.svg';
import { TableCell,Box,Checkbox, Tooltip } from '@mui/material';
import RebateSummaryIndicator from './RebateSummaryIndicator';

// pass row object 
const RebateSummaryRow = ({ row, count, tableHeader,stickyEnabled,rowIndex,selectableRows}) =>{
    // get redux persistant state from redux store 
    const company = useSelector((state) => state.company);
    const companyInfo = useSelector((state) => state.companyInfo);
    const dispatch = useDispatch();
    const { setCompany, setCompanyInfo } = bindActionCreators(actionCreators, dispatch);
    // get context variables from parent 
    const {
        setCommentModal, 
        setTrailModal,
        setPencilModal,
        setEditRow,
        checkAll, 
        setCheckAll, 
        workflowActionCheckedList, 
        setWorkflowActionCheckedList,
        checkBoxChange,
        workflowAction, 
        workflowActionStatus, 
        userRole, 
        eligiblePeriod
    } = useContext(PageContext);

    // initialize state 
    const [checked, setChecked] = useState(false);
    const navigate = useNavigate();
    const [showCheckbox, setShowCheckbox] = useState(false); 

    // event handler for comment icon click 
    const onCommentClick = () =>{
        // set edit row to be used in modal component
        setEditRow(editRow => editRow = row);
        // make modal visible using state variable from context 
        setCommentModal(commentModal => commentModal = true);
    }

    // event handler for trail icon click 
    const onTrailClick = () =>{
        // set edit row to be used in modal component
        setEditRow(editRow => editRow = row);
        // make modal visible using state variable from context 
        setTrailModal(trailModal => trailModal = true);
    }

    // event handler for pencil icon click 
    const onPencilClick = () =>{
        // set edit row to be used in modal component
        setEditRow(editRow => editRow = row);
        // make modal visible using state variable from context 
        setPencilModal(pencilModal => pencilModal = true);
    }

    // event handler for checkbox click 
    const onCheckboxClick = () =>{
        if (checked == false){
            // mark box with check using state variable 
            setChecked(checked => checked = true);
            // add from workflow action checked list
            setWorkflowActionCheckedList( workflowActionCheckedList => workflowActionCheckedList = [...workflowActionCheckedList, row]);   
        }
        else {
            // clear check from box using state variable 
            setChecked(checked => checked = false);
            // remove from workflow action checked list
            var index = workflowActionCheckedList.indexOf(row);
            setWorkflowActionCheckedList( workflowActionCheckedList => workflowActionCheckedList = 
                                [...workflowActionCheckedList.slice(0, index), ...workflowActionCheckedList.slice(index + 1)]);
            // if checkAll is true make false and block change to other sub components
            if (checkAll == true){
                setCheckAll(checkAll => checkAll = false);
            }
        }
       
    }

    const onRowClick = (input) =>{
        //event handler when anywhere in the row is clicked
        if(selectableRows &&  row['workflowtypeid'] === process.env.REACT_APP_REBATE_WORKFLOW_TYPE_ID){
            setCompany(input);
            setCompanyInfo(row);
            navigate("/CompanyPayoutViewPage");
        }
        else if (selectableRows && row['workflowtypeid'] === process.env.REACT_APP_BONUS_WORKFLOW_TYPE_ID) {
            setCompany(input);
            setCompanyInfo(row);
            navigate("/CompanyBonusViewPage");
        }
    }

    //  show status color
    const showStatus = (value) => {
        // set color based on value
        let color = '#4472C4'; 
        let textColor = 'white'
        if (value == 'Pending'){
            color = '#FFC700'; 
            textColor = 'black'; 
        }
        else if (value == 'Rejected'){
            color = '#EB0000'; 
            textColor = 'white'; 
        }
        else if (value == 'Initial Review'){
            color = '#7E79DD'; 
            textColor = 'white'; 
        }
        else if (value == 'Final Review'){
            color = '#00A6CA'
            textColor = 'white'; 
        }
        else if (value == 'Accepted'){
            color = '#008A00';
            textColor = 'white'; 
        }
        // show text with color
        return (
            <div className="d-flex justify-content-start align-items-center gap-2">
              <Box
                sx={{
                  minWidth: 12,
                  height: 12,
                  bgcolor: color,
                  borderRadius: 100,
                }}
              />
              <div>{value}</div>
            </div>
          );
    }

    const getRowClassName = (columnOrder) =>{
        // Pass sticky row class if columnOrder is 0 i.e first column and stickyEnabled Prop is true
        let classNameVar =  selectableRows 
        && (row['workflowtypeid'] === process.env.REACT_APP_REBATE_WORKFLOW_TYPE_ID 
            || row['workflowtypeid'] === process.env.REACT_APP_BONUS_WORKFLOW_TYPE_ID)
        ? "table-cell-clicky" :"";
        return stickyEnabled && columnOrder===0 ? `table-cell-sticky-row ${classNameVar}`: classNameVar;
    }

    // check box if status is that for the workflow action on check all
    useEffect(() => {
        if( 
            workflowActionStatus === process.env.REACT_APP_ACCEPTED_STATUS
            && row['status'] == workflowActionStatus && userRole === row['userroleid']
            && row['transmitted'] === false
            // check that row is eligible (last period in cycle exists)
            && row['eligible'] === true
        ){
            setChecked(checked => checked = checkAll);  
        }       
        else if (
            workflowActionStatus !== process.env.REACT_APP_ACCEPTED_STATUS
            && row['status'] == workflowActionStatus && userRole === row['userroleid']
            // check that row is eligible (last period in cycle exists)
            && row['eligible'] === true
        ) {
            setChecked(checked => checked = checkAll); 
        }
        else {
            setChecked(checked => checked = false); 
        }
    }, [checkBoxChange]);

    // only have box checked if it is in workflowActionCheckedList
    useEffect(() => {
        if (workflowActionCheckedList.includes(row) && row['status'] == workflowActionStatus){
            setChecked(checked => checked = true);
        }
        else{
            setChecked(checked => checked = false);
        }
    }, [row, workflowAction]);

    // useEffect to determine visibility of checkbox
    useEffect(() => {
         if (workflowActionStatus === row['status'] && userRole === row['userroleid']
            && workflowActionStatus === process.env.REACT_APP_ACCEPTED_STATUS
            && row['transmitted'] === false
            // check that row is eligible (last period in cycle exists)
            && row['eligible'] === true
        ) {
            setShowCheckbox(showCheckbox => showCheckbox = true);
        }
        // show checkbox for all other statuses. only show checkbox if user is entitled 
        else if(workflowActionStatus === row['status'] && userRole === row['userroleid']
            && workflowActionStatus !== process.env.REACT_APP_ACCEPTED_STATUS
            // check that row is eligible (last period in cycle exists)
            && row['eligible'] === true
        ) {
            setShowCheckbox(showCheckbox => showCheckbox = true); 
        }
        // if conditions aren't met do not show checkbox
        else {
            setShowCheckbox(showCheckbox => showCheckbox = false); 
        }
    }, [workflowActionStatus, userRole, row]); 

    return(
        <>
            {/* map logic over all columns in row object */}
            {tableHeader.map(el => 
                el['columnname'] == 'mastercompanyname' && ( row['workflowtypeid'] === process.env.REACT_APP_REBATE_WORKFLOW_TYPE_ID 
                || row['workflowtypeid'] === process.env.REACT_APP_BONUS_WORKFLOW_TYPE_ID ) ?
                    <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder']) + ' ' + process.env.REACT_APP_REBATE_SUMMARY_WIDTH}>
                        <Tooltip title={row[el['columnname']]} className='table-cell-link-row' arrow>
                            {row[el['columnname']]}
                        </Tooltip>
                    </TableCell>                              
                : el['columnname'] == 'grossspend' || el['columnname'] == 'grossinterchange' || el['columnname'] == 'incentiveamount'
                || el['columnname'] == 'minimumspend' ? 
                    <> 
                        {row[el['columnname']] == null ?
                            <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} align='right' className= {getRowClassName(el['columnorder'])}>None</TableCell>
                        :<TableCell onClick={()=>onRowClick(row['mastercompanyname'])} align='right' className= {getRowClassName(el['columnorder'])}>{moneyFormat(row[el['columnname']])}</TableCell>}
                    </>
                : el['columnname'] == 'blank' ? 
                    <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])} align='center'> 
                        <PageTooltip title='comment'>
                            <img src={ChatIcon} className='data-table-icon me-2' onClick={(e)=>{
                                e.stopPropagation(); // Prevents the TableCell's onClick from firing
                                onCommentClick(e);}}/> 
                        </PageTooltip>
                        <PageTooltip title='trail'>
                            <img src={AuditTrailIcon} className='data-table-icon me-2' onClick={(e)=>{
                                e.stopPropagation(); // Prevents the TableCell's onClick from firing
                                onTrailClick(e);}}/>
                        </PageTooltip>
                        {
                            row['userroleid'] == "2" ? <PageTooltip title='edit pay period'>
                                <img src={PencilIcon} className='data-table-icon' onClick={(e)=>{
                                    e.stopPropagation(); // Prevents the TableCell's onClick from firing
                                    onPencilClick(e);}}/>
                            </PageTooltip>:
                            <PageTooltip title='disabled'>
                                <img src={PencilDisabledIcon} className='data-table-icon' onClick={(e)=>{
                                    e.stopPropagation(); // Prevents the TableCell's onClick from firing
                                }}/>
                            </PageTooltip>
                        }
                    </TableCell>
                : el['columnname'] == 'checkbox' ?
                    <TableCell  sx={{
                        backgroundColor:
                        rowIndex%2!==0 ? "#E6EDF1": "#F7F9FA",
                    }}
                    className= {getRowClassName(el['columnorder'])}
                    >
                        {showCheckbox ?
                            <Checkbox sx={{padding:0}} checked={checked} onChange={(e)=>onCheckboxClick(e)}                            
                            checkedIcon = {<SelectedIcon/>}
                            icon = {<UnselectedIcon/>}/>
                            // else show nothing 
                        :<></>}
                    </TableCell>
                : el['columnname'] == 'status' ? 
                <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])}>
                    {showStatus(row[el['columnname']])}
                </TableCell>
                : el['columnname'] == 'delinquencyfield' ? 
                    row[el['columnname']] == true ? 
                    <>
                        <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])}>
                            <PageTooltip title='delinquent'>
                              <ReportProblemIcon />
                            </PageTooltip>
                        </TableCell>
                    </>
                    : <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])}></TableCell>
                : el['columnname'] === 'indicator' ? 
                    <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])}>
                        <RebateSummaryIndicator show={row['indicator']} ids={row['indicatorid']} tooltip={row['tooltip']} />
                    </TableCell>
                // standard display for other columns 
                : row[el['columnname']] == null ?
                    <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])}>None</TableCell>
                : <TableCell onClick={()=>onRowClick(row['mastercompanyname'])} className= {getRowClassName(el['columnorder'])}>{row[el['columnname']]}</TableCell>
            )}
        </>
    );
}

export default RebateSummaryRow;